import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence, useAnimation } from 'framer-motion';
import { Sparkles, Zap, Activity, Music, CheckCircle } from 'lucide-react';
import { useAcademyStore } from '../../../store/academyStore';
import { ModuleSuccess } from '../../ui/ModuleSuccess';
import { ModuleLayout } from '../../ui/ModuleLayout';

export const GamePolish = () => {
    const { completeModule } = useAcademyStore();
    const [completed, setCompleted] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const controls = useAnimation();

    // Juice Toggles
    const [juiceEnabled, setJuiceEnabled] = useState({
        particles: false,
        shake: false,
        squash: false,
        flash: false
    });

    const [trigger, setTrigger] = useState(0);
    const [showParticles, setShowParticles] = useState(false);
    const [impact, setImpact] = useState(false);

    // Handle "Jump" action
    const handleJump = async () => {
        setTrigger(t => t + 1);

        // 1. Particle Logic
        if (juiceEnabled.particles) {
            setShowParticles(true);
            setTimeout(() => setShowParticles(false), 500);
        }

        // 2. Flash Logic
        if (juiceEnabled.flash) {
            setImpact(true);
            setTimeout(() => setImpact(false), 100);
        }

        // 3. Animation Logic (The Juice!)
        // Jump UP (Stretch)
        await controls.start({
            y: -150,
            scaleX: juiceEnabled.squash ? 0.8 : 1,
            scaleY: juiceEnabled.squash ? 1.2 : 1,
            transition: { duration: 0.25, ease: "easeOut" }
        });

        // Fall DOWN (Squash)
        await controls.start({
            y: 0,
            scaleX: juiceEnabled.squash ? 1.25 : 1,
            scaleY: juiceEnabled.squash ? 0.75 : 1,
            transition: { type: "spring", stiffness: 400, damping: 15 }
        });

        // Return to Normal
        controls.start({
            y: 0,
            scaleX: 1,
            scaleY: 1,
            transition: { duration: 0.15 }
        });

        // Completion Logic: User enabled everything and tested it
        const allEnabled = Object.values(juiceEnabled).every(v => v);
        if (!completed && allEnabled && trigger >= 4) {
            setCompleted(true);
            completeModule('game-polish', 100);
            setShowSuccess(true);
        }
    };

    const toggle = (key: keyof typeof juiceEnabled) => {
        setJuiceEnabled(prev => ({ ...prev, [key]: !prev[key] }));
    };

    return (
        <ModuleLayout
            moduleId="game-polish"
            title="Juice It!"
            description="Add polish to make your game feel alive."
        >
            <div className={`bg-slate-900 min-h-full font-sans text-slate-100 p-8 transition-colors duration-100 ${impact ? 'bg-slate-800' : ''}`}>
                <div className="max-w-4xl mx-auto">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-12">

                        {/* Game View */}
                        <div className="flex flex-col gap-4">
                            <motion.div
                                className="bg-slate-800 rounded-3xl border-4 border-slate-700 h-[400px] relative overflow-hidden flex items-end justify-center pb-8 shadow-2xl"
                                animate={juiceEnabled.shake && showParticles ? { x: [-4, 4, -4, 4, 0] } : {}}
                                transition={{ duration: 0.2 }}
                            >
                                {/* Background Grid */}
                                <div className="absolute inset-0 opacity-10"
                                    style={{ backgroundImage: 'linear-gradient(slate-500 1px, transparent 1px), linear-gradient(90deg, slate-500 1px, transparent 1px)', backgroundSize: '40px 40px' }}
                                />

                                {/* Particles */}
                                <AnimatePresence>
                                    {showParticles && Array.from({ length: 8 }).map((_, i) => (
                                        <motion.div
                                            key={`${trigger}-${i}`}
                                            initial={{ opacity: 1, scale: 0.5, x: 0, y: 0 }}
                                            animate={{ opacity: 0, scale: 0, x: (Math.random() - 0.5) * 120, y: -Math.random() * 80 }}
                                            exit={{ opacity: 0 }}
                                            className="absolute bottom-8 w-4 h-4 bg-yellow-400 rounded-full"
                                        />
                                    ))}
                                </AnimatePresence>

                                {/* The Player */}
                                <motion.div
                                    animate={controls}
                                    className="w-16 h-16 bg-indigo-500 rounded-2xl relative z-10 shadow-lg shadow-indigo-500/50"
                                >
                                    {/* Eyes */}
                                    <div className="absolute top-4 left-3 w-3 h-3 bg-white rounded-full" />
                                    <div className="absolute top-4 right-3 w-3 h-3 bg-white rounded-full" />
                                </motion.div>
                            </motion.div>

                            <button
                                onClick={handleJump}
                                className="w-full py-6 rounded-2xl bg-indigo-600 hover:bg-indigo-500 text-white font-black text-2xl shadow-xl shadow-indigo-600/20 active:scale-95 transition-transform"
                            >
                                JUMP!
                            </button>
                        </div>

                        {/* Controls */}
                        <div className="space-y-8">
                            <div>
                                <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
                                    <Zap className="text-yellow-400" /> Juice Lab
                                </h2>
                                <p className="text-slate-400 mb-6">
                                    Toggle the features below to see how "Polish" changes the feel of the game.
                                </p>

                                <div className="space-y-3">
                                    {[
                                        { id: 'squash', icon: Music, label: 'Squash & Stretch', desc: 'Animation elasticity' },
                                        { id: 'particles', icon: Sparkles, label: 'Particles', desc: 'Dust clouds on jump' },
                                        { id: 'shake', icon: Activity, label: 'Screenshake', desc: 'Impact vibration' },
                                        { id: 'flash', icon: Zap, label: 'Screen Flash', desc: 'White fade on impact' },
                                    ].map((item) => (
                                        <button
                                            key={item.id}
                                            onClick={() => toggle(item.id as any)}
                                            className={`w-full p-4 rounded-xl border-2 flex items-center gap-4 transition-all ${juiceEnabled[item.id as keyof typeof juiceEnabled]
                                                ? 'bg-indigo-900/50 border-indigo-500 text-white'
                                                : 'bg-slate-800 border-slate-700 text-slate-500 hover:border-slate-500'
                                                }`}
                                        >
                                            <div className={`p-2 rounded-lg ${juiceEnabled[item.id as keyof typeof juiceEnabled] ? 'bg-indigo-500' : 'bg-slate-700'}`}>
                                                <item.icon size={20} />
                                            </div>
                                            <div className="text-left">
                                                <div className="font-bold">{item.label}</div>
                                                <div className="text-xs opacity-70">{item.desc}</div>
                                            </div>
                                            <div className="ml-auto">
                                                {juiceEnabled[item.id as keyof typeof juiceEnabled] && <CheckCircle size={20} className="text-green-400" />}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="bg-yellow-900/20 p-6 rounded-2xl border border-yellow-700/50">
                                <h3 className="font-bold text-yellow-400 mb-2">The "Game Feel"</h3>
                                <p className="text-sm text-yellow-100/70">
                                    Notice how the exact same "Jump" logic feels completely different with polish. This is the secret sauce of Game Dev.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {showSuccess && (
                    <ModuleSuccess
                        title="Maximum Juice!"
                        onRetry={() => setShowSuccess(false)}
                    >
                        <div className="space-y-4">
                            <p className="text-slate-600">
                                You turned a boring square into a living character.
                            </p>
                            <div className="bg-purple-50 p-4 rounded-xl border border-purple-100">
                                <h4 className="font-bold text-purple-800 text-sm mb-1">Developer Note</h4>
                                <p className="text-purple-700 text-xs">
                                    Great developers spend 50% of their time on the core mechanic and 50% on polish. It's what separates good games from great ones.
                                </p>
                            </div>
                        </div>
                    </ModuleSuccess>
                )}
            </div>
        </ModuleLayout>
    );
};
